import { Span } from '../../../shared_components/Library/Typeography'
import { useDispatch, useSelector } from 'react-redux'
import { Box } from '../../../shared_components/UI/Box'
import { useShouldShowForThreshold } from '../../../theme'
import styled from 'styled-components'
import {
  selectCurrentSeason,
  selectPastSeasonProgress,
  selectPastSeasons,
} from '../../../modules/seasons/selectors'
import Separator from '../../../shared_components/Library/Separator'
import { useEffect } from 'react'
import { setBottomNavButton } from '../../../modules/miscUi/actions'
import SeasonToggler from './Toggler'
import { Wrapper } from './Progress'
import { useHistory, useLocation } from 'react-router-dom'
import {
  setClaimPrizeTxHash,
  setPreviousSeasonFocusingOn,
} from '../../../modules/seasons/slice'
import AboutThisSeason from './About'
import SeasonComplete from './SeasonComplete'

export const UUIDV4_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/

export default function PreviousSeasons() {
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('medium')
  const dispatch = useDispatch()

  const pastSeasons = useSelector(selectPastSeasons)
  const serliazedPastSeasonProgress = useSelector(selectPastSeasonProgress)

  const location = useLocation()
  const history = useHistory()
  const previousSeasonId = location.pathname.split('/').pop()
  const isShowingPrevious = UUIDV4_REGEX.test(previousSeasonId)
  const previousSeason = pastSeasons.find(({ id }) => id === previousSeasonId)
  const claimPrizeTxHashFromPreviousSeason =
    serliazedPastSeasonProgress?.[previousSeasonId]?.claimPrizeTxHash
  const currentSeason = useSelector(selectCurrentSeason)

  useEffect(() => {
    if (!claimPrizeTxHashFromPreviousSeason) return
    dispatch(setClaimPrizeTxHash(claimPrizeTxHashFromPreviousSeason))
    return () => {
      dispatch(setClaimPrizeTxHash(null))
    }
  }, [dispatch, claimPrizeTxHashFromPreviousSeason])

  useEffect(() => {
    if (!isShowingPrevious || !previousSeason) return
    dispatch(setPreviousSeasonFocusingOn(previousSeason))
    return () => {
      dispatch(setPreviousSeasonFocusingOn(null))
    }
  }, [dispatch, previousSeason, isShowingPrevious])

  if (isShowingPrevious) return <PreviouslyWonSeasonScreen />

  return (
    <Page>
      <Wrapper dc g={32}>
        {!!currentSeason && isAtLeastMediumDisplaySize && <SeasonToggler />}
        <PastSeasonsTable dc>
          {!isAtLeastMediumDisplaySize && (
            <Row ac jsb>
              <Box flex={3}>
                <TitleSpan>Season</TitleSpan>
              </Box>
              <Box flex={2}>
                <TitleSpan>Outcome</TitleSpan>
              </Box>
              <Box flex={1} className="right" jfe>
                <TitleSpan ta="right">Tickets</TitleSpan>
              </Box>
            </Row>
          )}
          {pastSeasons
            .slice()
            .sort(
              (a, b) =>
                new Date(b.endDate).getTime() - new Date(a.endDate).getTime(),
            )
            .map((season, index) => {
              const { name, id: seasonId, endDate: endDateRaw } = season

              const tickets =
                serliazedPastSeasonProgress?.[seasonId]?.tickets ?? 0
              const entered = tickets > 0
              const won = serliazedPastSeasonProgress?.[seasonId]?.hasWon

              // lol when you just can't be arsed to read the docs
              const endDate = new Date(endDateRaw).toLocaleDateString()
              const endDateFormattd =
                endDate.split('/').join('.').split('20')[0] +
                endDate.split('20')[1]
              // if the above makes it to 2030 I'll be proud :skull"

              const outcomeColor = won
                ? 'function-success'
                : entered
                  ? 'function-error'
                  : 'text-secondary'

              const isLast = index === pastSeasons.length - 1

              function navToSeason() {
                const url = `/season/previous/${seasonId}`
                history.push(url)
              }

              return (
                <>
                  <Row key={seasonId} ac jsb>
                    <Box flex={3} dc>
                      <Span s="L" fw="semibold">
                        {name}
                      </Span>
                      <Span s="S" c="text-secondary">
                        {endDateFormattd}
                      </Span>
                    </Box>
                    <Box flex={2}>
                      <Span
                        c={outcomeColor}
                        onClick={won ? navToSeason : null}
                        fw={!entered ? 'regular' : 'semibold'}
                        style={{
                          cursor: won ? 'pointer' : 'unset',
                        }}
                      >
                        {!entered ? 'Non' : won ? 'Winner' : 'Lose'}
                      </Span>
                    </Box>
                    <Box flex={1} className="right" jfe>
                      <Span fw={entered ? 'semibold' : 'regular'} ta="right">
                        {tickets}
                      </Span>
                    </Box>
                  </Row>
                  {!isLast && <Separator />}
                </>
              )
            })}
        </PastSeasonsTable>
      </Wrapper>
    </Page>
  )
}

function PreviouslyWonSeasonScreen() {
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(setBottomNavButton(null))
    dispatch(setClaimPrizeTxHash(null))
    dispatch(setPreviousSeasonFocusingOn(null))
  }, [dispatch])
  return <SeasonComplete isShowingPrevious />
}

const Row = styled(Box)`
  .right {
    text-align: right !important;
  }
`

const TitleSpan = styled(Span)`
  color: var(--text-secondary, #b1b1b1);
  text-overflow: ellipsis;

  /* Web/Text/M/Regular */
  font-family: var(--type-family-space-grotesk, 'Space Grotesk');
  font-size: var(--Text-M-M-size, 14px);
  font-style: normal;
  font-weight: var(--type-weight-Regular, 300);
  line-height: var(--Text-M-M-line-height, 18px); /* 128.571% */
`

const PastSeasonsTable = styled(Box)`
  padding-top: var(--units-24, 24px);
  gap: 16px;
  flex-direction: column;
  @media screen and (min-width: ${(props) =>
      props.theme.breakpoints.medium}px) {
    padding: var(--Spacing-Padding-L, 20px);
    border-radius: var(--units-8, 8px);
    border: 1px solid var(--stroke-secondary, #333);
    background: var(--surface-primary, #080808);
  }
  @media screen and (max-width: ${(props) =>
      props.theme.breakpoints.medium}px) {
    padding: var(--units-24, 24px) var(--Index-Grids-Margins, 16px);
  }
`

const Page = styled(Box)`
  padding: var(--Index-Grids-Gutters) 0px var(--Index-Grids-Margins) 0px;
`
